import { ReactNode, forwardRef, useRef } from 'react';
import { ButtonProps } from '@/components/button/Button';
import { Popover as TinyPopover, ArrowContainer, PopoverPosition } from 'react-tiny-popover';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindConfig from 'tailwind.config';
import { DefaultColors } from 'tailwindcss/types/generated/colors';
import { ColorsType } from '@/style/theme/colors';

const tw = resolveConfig(tailwindConfig);
const { theme } = tw as unknown as {
  theme: (typeof tw)['theme'] & { colors: DefaultColors & ColorsType };
};

export interface PopoverBaseProps extends React.ComponentProps<'div'> {
  title?: string;
  headerChildren?: ReactNode;
  onClickOutside?: VoidFunction;
  setIsOpen?: VoidFunction;
  footerChildren?: ReactNode;
  popoverContent: JSX.Element;
  ctaPrimary?: Partial<ButtonProps>;
  ctaSecondary?: Partial<ButtonProps>;
  positions?: PopoverPosition | PopoverPosition[];
  arrowColor?: string;
  arrowSize?: number;
  isOpen?: boolean;
}

interface CustomComponentProps extends React.ComponentPropsWithoutRef<'div'> {
  onClick(): void;
}

export const PopoverChildComponent = forwardRef<HTMLDivElement, CustomComponentProps>(
  (props, ref) => {
    return (
      <div ref={ref} onClick={props.onClick}>
        {props.children}
      </div>
    );
  }
);

PopoverChildComponent.displayName = 'PopoverChildComponent';

const Popover = ({ ...props }: PopoverBaseProps) => {
  const customComponentRef = useRef(null);

  const {
    onClickOutside,
    children,
    popoverContent,
    positions,
    arrowColor,
    arrowSize,
    isOpen = false,
    setIsOpen,
  } = props;

  return (
    <TinyPopover
      ref={customComponentRef}
      isOpen={isOpen}
      positions={positions}
      containerClassName="z-50 animate-fadeIn"
      onClickOutside={() => {
        if (onClickOutside) onClickOutside();
      }}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor={arrowColor ?? theme.colors.dark[700]}
          arrowSize={arrowSize ?? 10}
          arrowStyle={{ opacity: 0.7 }}
          className="popover-arrow-container"
          arrowClassName="popover-arrow"
        >
          {popoverContent}
        </ArrowContainer>
      )}
    >
      <PopoverChildComponent
        ref={customComponentRef}
        onClick={() => {
          if (setIsOpen) setIsOpen();
        }}
      >
        {children}
      </PopoverChildComponent>
    </TinyPopover>
  );
};

export default Popover;
